// ─── SIDEBAR ───────────────────────────────────────────────────
const CLIENT_NAV_GROUPS = [
  [
    { id:'home', label:'Home', Icon: Home },
  ],
  [
    { id:'setup',    label:'Setup Guide', Icon: BookOpen },
    { id:'sources',  label:'Sources',  Icon: Upload   },
    { id:'products', label:'Products', Icon: Inbox    },
    { id:'orders',   label:'Orders',   Icon: Package  },
    { id:'export',   label:'Export',   Icon: Download },
  ],
  [
    { id:'analytics', label:'Analytics', Icon: BarChart2 },
    { id:'support',   label:'Support',   Icon: LifeBuoy },
  ],
  [
    { id:'settings', label:'Settings', Icon: Settings },
  ],
];

const ADMIN_NAV_GROUPS = [
  [
    { id:'home', label:'Overview', Icon: Home, adminOnly:true },
    { id:'users', label:'Users', Icon: Users, summaryKey:'users', adminOnly:true },
    { id:'support', label:'Support Inbox', Icon: ShieldCheck, summaryKey:'unansweredTickets', adminOnly:true },
    { id:'delivery-logs', label:'Delivery Logs', Icon: Truck, adminOnly:true },
    { id:'ocf', label:'OCF Review', Icon: SlidersHorizontal, adminOnly:true },
    { id:'settings', label:'Settings', Icon: Settings, adminOnly:true },
  ],
];

const useAppVersion = () => {
  const [version, setVersion] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      for (const path of ['/version.json', '/api/version']) {
        try {
          const res = await fetch(path, { cache: 'no-store' });
          if (!res.ok) continue;
          const data = await res.json();
          if (active && data?.version) {
            setVersion(data.version);
            return;
          }
        } catch {}
      }
    };
    load();
    return () => { active = false; };
  }, []);

  return version;
};

const useAdminSidebarSummary = isAdmin => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    let timer = null;

    if (!isAdmin) {
      setSummary(null);
      setLoading(false);
      return () => {};
    }

    const load = async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      const { ok, data } = await api('GET', '/api/admin/sidebar-summary');
      if (!active) return;
      if (ok) setSummary(data || {});
      setLoading(false);
    };

    load();
    timer = setInterval(() => load({ quiet:true }), 30000);
    const refresh = () => load({ quiet:true });
    window.addEventListener('focus', refresh);
    window.addEventListener('omnifill:admin-summary-refresh', refresh);

    return () => {
      active = false;
      if (timer) clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('omnifill:admin-summary-refresh', refresh);
    };
  }, [isAdmin]);

  return { summary, loading };
};

const Sidebar = ({ active, onNavigate, sidebarBg, user, onLogout, setupGuide, mobileOpen = false, onMobileClose }) => {
  const appVersion = useAppVersion();
  const hasInlineAdminItems = CLIENT_NAV_GROUPS.some(group => group.some(item => item.adminOnly));
  const { summary: adminSummary, loading: adminSummaryLoading } = useAdminSidebarSummary(!!user?.isAdmin && hasInlineAdminItems);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [rememberedAccounts, setRememberedAccounts] = useState(() => readRememberedAccounts());
  const accountMenuRef = useRef(null);

  useEffect(() => {
    setRememberedAccounts(rememberAccount(user));
  }, [user?.email, user?.isAdmin]);

  useEffect(() => {
    if (!accountMenuOpen) return;
    const onPointerDown = event => {
      if (!accountMenuRef.current?.contains(event.target)) setAccountMenuOpen(false);
    };
    const onKeyDown = event => {
      if (event.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [accountMenuOpen]);

  const currentAccountKey = user?.email || '';
  const accountRows = rememberedAccounts.length ? rememberedAccounts : [normalizeRememberedAccount(user)].filter(account => account.email);
  const setupDone = setupGuide?.progress?.done ?? 0;
  const setupTotal = setupGuide?.progress?.total ?? 6;
  const setupComplete = setupTotal > 0 && setupDone >= setupTotal && !setupGuide?.progress?.loading;
  const visibleNavGroups = CLIENT_NAV_GROUPS
    .map(group => group.filter(item => (!item.adminOnly || user?.isAdmin) && (item.id !== 'setup' || (!setupGuide?.dismissed && !setupComplete))))
    .filter(group => group.length);
  const getNavSummaryCount = item => {
    if (!item.summaryKey || !adminSummary) return null;
    const count = Number(adminSummary[item.summaryKey]);
    return Number.isFinite(count) ? count : null;
  };

  const handleAddAccount = async () => {
    setAccountMenuOpen(false);
    clearPendingAccountSwitch();
    await onLogout();
  };

  const handleSwitchAccount = async account => {
    const key = account.email;
    if (!key || key === currentAccountKey) return;
    setAccountMenuOpen(false);
    setPendingAccountSwitch(account);
    await onLogout();
  };

  return (
    <aside className={`dashboard-sidebar${mobileOpen ? ' is-mobile-open' : ''}`} style={{ background: sidebarBg||'#1E2433' }} aria-label="Main navigation">
      <div className="dashboard-sidebar-header" style={{ padding:'22px 20px 20px' }}>
        <button onClick={() => onNavigate('home')} style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', gap:9 }}>
          <Logo size={18}/>
        </button>
        <button type="button" className="dashboard-sidebar-close" onClick={onMobileClose} aria-label="Close navigation">
          <X size={20} strokeWidth={1.9}/>
        </button>
      </div>
      <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop:8, flex:1 }}>
        {visibleNavGroups.map((group, groupIdx) => (
          <div key={groupIdx} style={{ borderTop: groupIdx === 0 ? 'none' : '1px solid rgba(255,255,255,0.08)', paddingTop: groupIdx === 0 ? 0 : 8, marginTop: groupIdx === 0 ? 0 : 8 }}>
            {group.every(item => item.adminOnly) && (
              <div style={{ padding:'3px 20px 7px', fontSize:10, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:0, display:'flex', alignItems:'center', gap:6 }}>
                <ShieldCheck size={11} strokeWidth={2}/>
                <span>Admin</span>
              </div>
            )}
            {group.map(item => {
              const isActive = active === item.id;
              const summaryCount = getNavSummaryCount(item);
              if (item.id === 'setup') {
                const done = setupGuide?.progress?.done ?? 0;
                const total = setupGuide?.progress?.total ?? 6;
                const progressLoading = setupGuide?.progress?.loading && !setupGuide?.progress?.stale;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={item.id} style={{
                    width:'100%',
                  }}>
                    <div style={{
                      width:'100%',
                      display:'flex',
                      alignItems:'center',
                      background: isActive ? 'rgba(0,111,221,0.12)' : 'transparent',
                      borderLeft:`2px solid ${isActive ? '#006FDD' : 'transparent'}`,
                      transition:'all 0.12s',
                    }}>
                      <button onClick={() => onNavigate(item.id)} style={{
                        flex:1, minWidth:0, display:'flex', alignItems:'center', gap:10, padding:'8px 0 8px 20px',
                        background:'transparent', border:'none', color:isActive ? '#fff' : '#9CA3AF',
                        fontSize:14, fontWeight:isActive ? 600 : 400, cursor:'pointer', textAlign:'left',
                      }}>
                        <span style={{ opacity:isActive ? 1 : 0.7 }}><item.Icon size={16} strokeWidth={1.7}/></span>
                        <span style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:5 }}>
                          <span style={{ minWidth:0, whiteSpace:'nowrap' }}>{item.label}</span>
                          <span title={progressLoading ? 'Loading setup progress' : `${done} of ${total} setup steps complete`} style={{ display:'flex', alignItems:'center', gap:6, color:isActive ? '#D1D5DB' : '#7B8493' }}>
                            <span style={{ flex:1, minWidth:34, height:4, background:'rgba(255,255,255,0.12)', borderRadius:4, overflow:'hidden' }}>
                              {!progressLoading && <span style={{ display:'block', width:`${pct}%`, height:'100%', background:'#73D04B', borderRadius:4 }}></span>}
                            </span>
                            {progressLoading
                              ? <SkeletonPill width={24} height={10} style={{ flexShrink:0, opacity:0.34 }}/>
                              : <span style={{ flexShrink:0, fontSize:10, fontWeight:800, lineHeight:1 }}>{done}/{total}</span>
                            }
                          </span>
                        </span>
                      </button>
                      <button onClick={setupGuide?.onDismiss} title="Dismiss setup guide" style={{ width:28, height:28, marginRight:14, border:'none', background:'transparent', color:'#6B7280', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                        <X size={13} strokeWidth={2.1}/>
                      </button>
                    </div>
                  </div>
                );
              }
              return (
                <button key={item.id} onClick={() => {
                  if (item.disabled) return;
                  onNavigate(item.id);
                }} aria-disabled={item.disabled ? 'true' : undefined} title={item.disabled ? `${item.label} coming soon` : undefined} style={{
                  width:'100%', display:'flex', alignItems:'center', gap:10, padding:'10px 20px',
                  background: isActive ? 'rgba(0,111,221,0.12)' : 'transparent',
                  border:'none', borderLeft: `2px solid ${isActive ? '#006FDD' : 'transparent'}`,
                  color: item.disabled ? '#6B7280' : (isActive ? '#fff' : '#9CA3AF'),
                  fontSize:14, fontWeight: isActive ? 600 : 400, cursor: item.disabled ? 'default' : 'pointer',
                  textAlign:'left', transition:'all 0.12s',
                }}>
                  <span style={{ opacity: isActive ? 1 : 0.7 }}><item.Icon size={16} strokeWidth={1.7}/></span>
                  <span style={{ flex:1, minWidth:0, whiteSpace:'nowrap' }}>{item.label}</span>
                  {item.summaryKey && adminSummaryLoading && summaryCount === null && <SkeletonPill width={22} height={16} style={{ flexShrink:0, opacity:0.34 }}/>}
                  {summaryCount !== null && (
                    <span title={item.summaryKey === 'unansweredTickets' ? `${summaryCount} ticket${summaryCount === 1 ? '' : 's'} waiting on support` : `${summaryCount} user${summaryCount === 1 ? '' : 's'}`} style={{
                      flexShrink:0,
                      minWidth:22,
                      height:18,
                      padding:'0 6px',
                      borderRadius:999,
                      background:isActive ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.08)',
                      color:isActive ? '#F9FAFB' : '#A7B0C0',
                      fontSize:11,
                      fontWeight:800,
                      lineHeight:1,
                      display:'inline-flex',
                      alignItems:'center',
                      justifyContent:'center',
                    }}>{summaryCount}</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div style={{ padding:'18px 22px 16px', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
        {user?.isAdmin && (
          <a href="https://admin.omnifill.net" style={{ width:'100%', background:'rgba(0,111,221,0.18)', border:'1px solid rgba(0,111,221,0.28)', borderRadius:7, padding:'8px 11px', fontSize:12, color:'#E8F1FF', cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:8, marginBottom:10, textDecoration:'none', fontWeight:800 }}>
            <ShieldCheck size={13} strokeWidth={1.8}/> Admin Portal
          </a>
        )}
        <a href="https://omnifill.net/docs" target="_blank" rel="noreferrer" style={{ width:'100%', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.04)', borderRadius:7, padding:'8px 11px', fontSize:12, color:'#D1D5DB', cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
          <BookOpen size={13} strokeWidth={1.8}/> Documentation
        </a>
        <div ref={accountMenuRef} style={{ position:'relative', marginBottom:12 }}>
          <button type="button" onClick={() => setAccountMenuOpen(open => !open)} aria-haspopup="menu" aria-expanded={accountMenuOpen ? 'true' : 'false'} style={{ width:'100%', background: accountMenuOpen ? 'rgba(0,111,221,0.16)' : 'rgba(255,255,255,0.07)', border:`1px solid ${accountMenuOpen ? 'rgba(0,111,221,0.44)' : 'rgba(255,255,255,0.06)'}`, borderRadius:8, padding:'10px 11px', cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:8, transition:'background 0.12s, border-color 0.12s' }}>
            <span style={{ minWidth:0, flex:1 }}>
              <span style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, fontSize:10, color:'#8B95A5', marginBottom:2 }}>
                <span>Signed in as</span>
                {user?.isAdmin && <span style={{ flexShrink:0, color:'#9CA3AF', fontWeight:600 }}>ADMIN</span>}
              </span>
              <span title={user?.email || undefined} style={{ display:'block', minWidth:'15ch', maxWidth:'100%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:13, color:'#F9FAFB', fontWeight:700, lineHeight:1.2 }}>{user?.email || 'Guest'}</span>
            </span>
            <span style={{ color:'#9CA3AF', flexShrink:0, transform: accountMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform 0.12s' }}>
              <ChevronDown size={15} strokeWidth={1.9}/>
            </span>
          </button>
          {accountMenuOpen && (
            <div role="menu" style={{ position:'absolute', left:0, bottom:'calc(100% + 10px)', width:276, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'0 18px 48px rgba(15,23,42,0.24)', zIndex:40, overflow:'hidden' }}>
              <div style={{ padding:'11px 12px', borderBottom:'1px solid #F3F4F6' }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#111827' }}>Accounts</div>
                <div style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>Switching asks you to sign in again.</div>
              </div>
              <div style={{ maxHeight:220, overflowY:'auto', padding:'6px' }}>
                {accountRows.map(account => {
                  const key = account.email;
                  const isCurrent = key === currentAccountKey;
                  return (
                    <button key={key} type="button" role="menuitem" onClick={() => handleSwitchAccount(account)} disabled={isCurrent} style={{ width:'100%', border:'none', background: isCurrent ? '#F8FAFC' : 'transparent', borderRadius:6, padding:'8px', display:'flex', alignItems:'center', gap:8, textAlign:'left', cursor: isCurrent ? 'default' : 'pointer' }}>
                      <UserCircle size={17} strokeWidth={1.8} color={isCurrent ? '#006FDD' : '#6B7280'}/>
                      <span style={{ minWidth:0, flex:1 }}>
                        <span style={{ display:'block', fontSize:12, fontWeight:700, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{account.email || 'Account'}</span>
                        <span style={{ display:'block', fontSize:11, color:'#6B7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{isCurrent ? 'Current account' : 'Saved account'}</span>
                      </span>
                      {isCurrent && <Check size={15} strokeWidth={2} color="#006FDD"/>}
                    </button>
                  );
                })}
              </div>
              <div style={{ borderTop:'1px solid #F3F4F6', padding:6 }}>
                <button type="button" role="menuitem" onClick={handleAddAccount} style={{ width:'100%', border:'none', background:'transparent', borderRadius:6, padding:'8px', display:'flex', alignItems:'center', gap:8, textAlign:'left', cursor:'pointer', color:'#111827', fontSize:12, fontWeight:700 }}>
                  <Plus size={16} strokeWidth={2}/> Add account
                </button>
              </div>
            </div>
          )}
        </div>
        <button onClick={onLogout} style={{ width:'100%', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.04)', borderRadius:7, padding:'8px 11px', fontSize:12, color:'#D1D5DB', cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:8 }}>
          <LogOut size={13} strokeWidth={1.8}/> Sign out
        </button>
        <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', marginTop:14, paddingTop:12, fontSize:10, color:'#6B7280', letterSpacing:'0.03em', textTransform:'uppercase' }}>
          {appVersion ? `Omnifill v${appVersion}` : 'Omnifill'}
        </div>
      </div>
    </aside>
  );
};
